import { useContext, useMemo, useState } from 'react';
import { AdminDashboardContext } from '../contexts';
import {
  useAssignedTasksMetricsQuery,
  useAverageTrackedMinutesMetricsQuery,
  useCompletedTasksMetricsQuery,
  useCreatedTasksMetricsQuery,
  useHighPriorityTasksMetricsQuery,
  useLowPriorityTasksMetricsQuery,
  useMediumPriorityTasksMetricsQuery,
  useOpenTasksMetricsQuery,
  useOverdueTasksMetricsQuery,
  usePrematurelyCompletedTasksMetricsQuery,
  useTasksByPriorityMetricsQuery,
  useTasksMetricsQuery,
  useTrackedMinutesMetricsQuery,
  useUniqueTaskEditsMetricsQuery,
  useUrgentTasksMetricsQuery,
} from '@common/cache/queries/metrics';
import useUserStore from 'store/user.store';
import { theme } from '@store';

type taskChartsType =
  | 'minutes'
  | 'assignedTasks'
  | 'completedTasks'
  | 'taskByPriority'
  | 'overdueTasks'
  | 'urgentTasks'
  | 'highPriorityTasks'
  | 'mediumPriorityTasks'
  | 'lowPriorityTasks'
  | 'uniqueTasks'
  | 'prematurelyClosedTasks'
  | 'openTasks'
  | 'createdTasks';

export type TaskLabelType = {
  label: string;
  color: string;
};

const taskKeyToLabel: { [key: string]: TaskLabelType } = {
  value1: {
    label: 'Completed',
    color: theme.palette.primary.light,
  },
  value2: {
    label: 'Opened',
    color: theme.palette.primary.main,
  },
};

const sortByInitialValue: Record<taskChartsType, 'ASC' | 'DESC'> = {
  minutes: 'DESC',
  assignedTasks: 'DESC',
  completedTasks: 'DESC',
  taskByPriority: 'DESC',
  overdueTasks: 'DESC',
  urgentTasks: 'DESC',
  highPriorityTasks: 'DESC',
  mediumPriorityTasks: 'DESC',
  lowPriorityTasks: 'DESC',
  uniqueTasks: 'DESC',
  prematurelyClosedTasks: 'DESC',
  openTasks: 'DESC',
  createdTasks: 'DESC',
};

export const useTaskCharts = () => {
  const [tasksChartSortByKey, setTasksChartSortByKey] =
    useState(sortByInitialValue);
  const { user } = useUserStore();
  const { filters } = useContext(AdminDashboardContext);
  const { interval, to_date, from_date } = filters;

  const { data: trackedMinutes, isLoading: isLoadingMinutes } =
    useTrackedMinutesMetricsQuery({
      supervisor_ids: filters.supervisor_ids,
      record_ids: filters.record_ids,
      interval,
      to_date,
      from_date,
      sort_order: tasksChartSortByKey.minutes,
    });

  const { data: averageTrackedMinutes, isLoading: isLoadingAvgMinutes } =
    useAverageTrackedMinutesMetricsQuery({
      supervisor_ids: filters.supervisor_ids,
      record_ids: filters.record_ids,
      interval,
      to_date,
      from_date,
      sort_order: tasksChartSortByKey.minutes,
    });

  const {
    data: taskMetrics = { data: [], linked_report_id: '' },
    isLoading: isLoadingTaskMetrics,
  } = useTasksMetricsQuery({
    supervisor_ids: filters.supervisor_ids,
    record_ids: filters.record_ids,
    interval,
    to_date,
    from_date,
  });
  const { data: assignedTasks, isLoading: isLoadingAssigned } =
    useAssignedTasksMetricsQuery({
      supervisor_ids: filters.supervisor_ids,
      record_ids: filters.record_ids,
      interval,
      to_date,
      from_date,
      sort_order: tasksChartSortByKey.assignedTasks,
    });
  const { data: completedTasks, isLoading: isLoadingCompleted } =
    useCompletedTasksMetricsQuery({
      supervisor_ids: filters.supervisor_ids,
      record_ids: filters.record_ids,
      interval,
      to_date,
      from_date,
      sort_order: tasksChartSortByKey.completedTasks,
    });
  const { data: tasksByPriority, isLoading: isLoadingByPriority } =
    useTasksByPriorityMetricsQuery({
      supervisor_ids: filters.supervisor_ids,
      record_ids: filters.record_ids,
      interval,
      to_date,
      from_date,
      sort_order: tasksChartSortByKey.taskByPriority,
    });
  const { data: overdueTasks, isLoading: isLoadingOverdue } =
    useOverdueTasksMetricsQuery({
      supervisor_ids: filters.supervisor_ids,
      record_ids: filters.record_ids,
      interval,
      to_date,
      from_date,
      sort_order: tasksChartSortByKey.overdueTasks,
    });
  const { data: urgentTasks, isLoading: isLoadingUrgent } =
    useUrgentTasksMetricsQuery({
      supervisor_ids: filters.supervisor_ids,
      record_ids: filters.record_ids,
      interval,
      to_date,
      from_date,
      sort_order: tasksChartSortByKey.urgentTasks,
    });
  const { data: mediumPriorityTasks, isLoading: isLoadingMedium } =
    useMediumPriorityTasksMetricsQuery({
      supervisor_ids: filters.supervisor_ids,
      record_ids: filters.record_ids,
      interval,
      to_date,
      from_date,
      sort_order: tasksChartSortByKey.mediumPriorityTasks,
    });
  const { data: lowPriorityTasks, isLoading: isLoadingLow } =
    useLowPriorityTasksMetricsQuery({
      supervisor_ids: filters.supervisor_ids,
      record_ids: filters.record_ids,
      interval,
      to_date,
      from_date,
      sort_order: tasksChartSortByKey.lowPriorityTasks,
    });
  const { data: highPriorityTasks, isLoading: isLoadingHigh } =
    useHighPriorityTasksMetricsQuery({
      supervisor_ids: filters.supervisor_ids,
      record_ids: filters.record_ids,
      interval,
      to_date,
      from_date,
      sort_order: tasksChartSortByKey.highPriorityTasks,
    });

  const { data: uniqueTaskEdits, isLoading: isLoadingUnique } =
    useUniqueTaskEditsMetricsQuery({
      supervisor_ids: filters.supervisor_ids,
      record_ids: filters.record_ids,
      interval,
      to_date,
      from_date,
      sort_order: tasksChartSortByKey.uniqueTasks,
    });
  const { data: prematurelyCompletedTasks, isLoading: isLoadingPremature } =
    usePrematurelyCompletedTasksMetricsQuery({
      supervisor_ids: filters.supervisor_ids,
      record_ids: filters.record_ids,
      interval,
      to_date,
      from_date,
      sort_order: tasksChartSortByKey.prematurelyClosedTasks,
    });
  const { data: openTasks, isLoading: isLoadingOpen } =
    useOpenTasksMetricsQuery({
      supervisor_ids: filters.supervisor_ids,
      record_ids: filters.record_ids,
      interval,
      to_date,
      from_date,
      sort_order: tasksChartSortByKey.openTasks,
    });
  const { data: createdTasks, isLoading: isLoadingCreated } =
    useCreatedTasksMetricsQuery({
      supervisor_ids: filters.supervisor_ids,
      record_ids: filters.record_ids,
      interval,
      to_date,
      from_date,
      sort_order: tasksChartSortByKey.createdTasks,
    });

  const hasViewPermission = user?.permissions.includes('view_reports');

  const handleChangeSortByKey = (key: taskChartsType) => {
    setTasksChartSortByKey(prevState => ({
      ...prevState,
      [key]: prevState[key] === 'ASC' ? 'DESC' : 'ASC',
    }));
  };

  const fullReportUrl = useMemo(() => {
    const id = taskMetrics?.linked_report_id;
    if (!id) return '';
    const params = new URLSearchParams();
    if (interval) {
      params.set('interval', interval);
      if (interval === 'custom') {
        if (from_date) params.set('from_date', from_date);
        if (to_date) params.set('to_date', to_date);
      }
    }
    const query = params.toString();
    return `/reporting/items/${id}${query ? `?${query}` : ''}`;
  }, [taskMetrics?.linked_report_id, interval, to_date, from_date]);

  return {
    hasViewPermission,
    trackedMinutes,
    averageTrackedMinutes,
    createdTasks,
    openTasks,
    assignedTasks,
    mediumPriorityTasks,
    lowPriorityTasks,
    highPriorityTasks,
    urgentTasks,
    prematurelyCompletedTasks,
    uniqueTaskEdits,
    completedTasks,
    tasksByPriority,
    overdueTasks,
    tasksChartSortByKey,
    handleChangeSortByKey,
    isLoadingTaskMetrics,
    isLoadingMinutes,
    isLoadingAvgMinutes,
    isLoadingAssigned,
    isLoadingCompleted,
    isLoadingByPriority,
    isLoadingOverdue,
    isLoadingUrgent,
    isLoadingMedium,
    isLoadingLow,
    isLoadingHigh,
    isLoadingUnique,
    isLoadingPremature,
    isLoadingOpen,
    isLoadingCreated,
    taskMetrics: taskMetrics.data.map(task => {
      return {
        value1: task.completed_tasks_count,
        value2: task.created_tasks_count,
        label: task.label,
      };
    }),
    taskMetricsLinkedReportId: taskMetrics?.linked_report_id,
    taskKeyToLabel,
    taskYAxisMaxValue: taskMetrics.data.reduce((acc, curr) => {
      const sum = curr.completed_tasks_count + curr.created_tasks_count;
      return sum > acc ? sum : acc;
    }, 0),
    fullReportUrl,
  };
};
